// The JSON the Razor components send. src/Cricket.UI/Drawing/Inputs.cs mirrors these one to one.

export type Line = 'Wide' | 'Off' | 'Stumps' | 'Leg' | 'DownLeg';
export type Length = 'Yorker' | 'Full' | 'Good' | 'Back' | 'Short';
export type Kind = 'Stock' | 'Variation';
export type Shot = 'Defend' | 'Rotate' | 'Ground' | 'Aerial' | 'Leave';
export type Contact = 'Clean' | 'Mistimed' | 'Edge' | 'Miss' | 'Pad';
export type Zone = 'ThirdMan' | 'Point' | 'Cover' | 'MidOff' | 'MidOn' | 'Midwicket' | 'SquareLeg' | 'FineLeg' | 'Cordon';
export type Extra = 'Wide' | 'NoBall' | 'Bye';
export type Dismissal = 'Bowled' | 'Caught' | 'Lbw' | 'Stumped' | 'RunOut';
export type ChanceKind = 'Catch' | 'Stumping' | 'RunOut';
export type LbwReason = 'HittingTheStumps' | 'GoingOver' | 'MissingLeg' | 'OutsideTheLine' | 'MissingOff';
export type Release = 'OverClose' | 'OverWide' | 'Round';
export type Angle = 'In' | 'Straight' | 'Across';
export type Bounce = 'Low' | 'Normal' | 'High';
export type Guard = 'Leg' | 'Middle';
export type Feet = 'Front' | 'Back' | 'Advance' | 'Across';
export type Stroke =
  | 'ForwardDefence' | 'BackFootDefence' | 'Leave' | 'Glance' | 'Flick' | 'Nudge' | 'Push' | 'Dab' | 'Sweep' | 'Pull'
  | 'CoverDrive' | 'OffDrive' | 'StraightDrive' | 'OnDrive' | 'SquareCut' | 'BackFootPunch' | 'LoftedDrive' | 'Hook' | 'SlogSweep' | 'UpperCut';
export type Position = 'Slip1' | 'Slip2' | 'Slip3' | 'Gully' | 'ThirdMan' | 'Point' | 'DeepPoint' | 'Cover' | 'ExtraCover' | 'DeepCover'
  | 'MidOff' | 'LongOff' | 'MidOn' | 'LongOn' | 'Midwicket' | 'DeepMidwicket' | 'SquareLeg' | 'DeepSquareLeg' | 'ShortFineLeg' | 'FineLeg';
export type PresetName = 'AttackingNewBall' | 'TwoSlips' | 'RingField' | 'SpinRing' | 'BoundaryRiders' | 'OffSideTrap' | 'LegSidePressure' | 'DeathSpread';

export interface Intended { line: Line; length: Length; kind: Kind; release: Release }
export interface Delivered { line: Line; length: Length; kind: Kind; angle: Angle; bounce: Bounce; drifted: boolean; extra: Extra | null }
/** The shot step: the class that played, the feet (null for a leave), the stroke, and the class chosen first when the batter fell back. */
export interface Played { class: Shot; feet: Feet | null; stroke: Stroke; chosen: Shot | null }
export interface ChainRecord {
  intended: Intended; delivered: Delivered;
  read: { as: Kind; misread: boolean } | null;
  shot: Played | null; contact: Contact | null;
  ball: { zone: Zone | null; fielder: string | null };
  chance: { kind: ChanceKind; difficulty: number; fielder: string; taken: boolean } | null;
  decision: { out: boolean; reason: LbwReason } | null;
  result: { bat: number; extras: number; extraKind: Extra | null; legal: boolean; boundary: number | null; dismissal: Dismissal | null; dismissed: 'Striker' | 'NonStriker' | null; fielder: string | null };
}

export interface Person { id: string; initials: string; name: string }
export interface Fielder extends Person { position: Position }
export interface BallPath { zone: Zone | null; aerial: boolean; runs: number; boundary: number | null; out: boolean; fielder: string | null }
export interface Move { id: string; from: Position; to: Position }
/** leftHanded: the striker bats left-handed, so the field changes over and every position mirrors. */
export interface OvalInput {
  label: string;
  preset: PresetName;
  fielders: Fielder[];
  keeper: Person; keeperUp: boolean; bowler: Person; striker: Person; nonStriker: Person;
  ball: BallPath | null;
  zones: Zone[];
  names: boolean;
  moves: Move[];
  leftHanded: boolean;
}
/** guard and feet are null when the record has none (a wide, or a ball not yet played). */
export interface PitchMapInput { label: string; intended: Intended; delivered: Delivered; mini: boolean; leftHanded: boolean; guard: Guard | null; feet: Feet | null }
export interface ChainVizInput { keys: string[]; chain: ChainRecord; oval: OvalInput; people: Person[]; guard: Guard }
export interface BallScore { over: number; label: string; runs: number; wickets: number }
export interface ScoreInput { label: string; startOver: number; overs: number; start: { runs: number; wickets: number }; balls: BallScore[]; color: string }

/** The mockup's palette (K.C). */
export const C = {
  gold: '#c9a227', goldL: '#f0c846', harbor: '#4aa3df', valley: '#e0604f',
  ok: '#5cc98a', warn: '#e8a33d', bad: '#e0604f', ink: '#0b160c',
};

let seq = 0;
/** A page-unique id for defs (K.uid). */
export const uid = (): string => 'k' + ++seq;

/** Escapes text and attribute values that came from data. */
export const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
